import { EntityTypes } from '@/jbi-shared/types/jaas-permission.types';
import { RouteDTO } from '@/jbi-shared/types/Nav';
import { CustomNavLink, jtmsNavigationLinks, myJBINavbarLinks } from '@/router';
import { ApplicationContext } from '@/store/modules/application-context/types/application-context.types';
import { ModuleTreeRecord } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { RootState } from '@/store/store';
import { ApiState } from '@/store/types/general.types';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { Action, Getter, State } from 'vuex-class';

@Component({})
export class RbacMixin extends Vue {
  @Action('rbac/getMyRoles')
  public getMyRoles!: () => void;

  @Action('rbac/getUserPermissionsMatrix')
  public getUserPermissionsMatrix!: () => void;

  @Action('profile/getMe')
  public getMe!: () => void;

  @Action('auth/silentSessionHealthCheck')
  public silentSessionHealthCheck!: () => void;

  @State(({ profile }: RootState) => profile.apiState.getMe)
  public getMeApiState!: ApiState;

  @State(({ auth }: RootState) => auth.isLoggedIn)
  public isLoggedIn!: boolean;

  @State((state: RootState) => state.rbac.apiState.getMyRoles)
  public getMyRolesState!: ApiState;

  @State((state: RootState) => state.rbac.roles)
  public roles!: string;

  @State((state: RootState) => state.rbac.apiState.getUserPermissionsMatrix)
  public getUserPermissionsMatrixState!: ApiState;

  @Action('rolesAndPermissions/getModules')
  public getModules!: () => Promise<ModuleTreeRecord>;

  @State(({ rolesAndPermissions }: RootState) => rolesAndPermissions.modules)
  public modules!: ModuleTreeRecord[];

  @State(
    ({ applicationContext }: RootState) => applicationContext.applicationContext
  )
  public applicationContext!: ApplicationContext;

  @State(({ auth }: RootState) => auth.apiState.checkSessionHealth)
  public silentSessionHealthCheckApiState!: ApiState;

  @Getter('rbac/isAdmin')
  public isAdmin!: boolean;

  @Getter('rbac/rootModules')
  public rootModules!: string[];

  @Getter('rbac/readModules')
  public readModules!: string[];

  private filteredNavLinks: CustomNavLink[] = [];

  created() {
    if (!this.silentSessionHealthCheckApiState.loading) {
      this.silentSessionHealthCheck();
    }
  }

  // Add watcher to method to update state variables based on user
  @Watch('isLoggedIn', { immediate: true })
  onUserLoggedIn() {
    if (this.isLoggedIn) {
      if (!this.getMyRolesState.loading) {
        this.getMyRoles();
      }

      if (!this.getUserPermissionsMatrixState.loading) {
        this.getUserPermissionsMatrix();
        this.getModules();
      }

      if (!this.getMeApiState.loading) {
        this.getMe();
      }
    }
  }

  /**
   * Navigation links are based on the user's permissions matrix. They may have
   * direct access to a module, implicit access via submodules or access through
   * instance (user has access to groups 1, 2, 3 only for example).
   */
  public getNavLinks(): CustomNavLink[] {
    const navLinks: CustomNavLink[] = this.getNavigationLinksBasedOnContext();
    this.filteredNavLinks = [];

    if (this.isAdmin) {
      return navLinks;
    }

    for (const link of navLinks) {
      const module = (link.to as Partial<RouteDTO>)?.module;
      const instanceType = (link.to as Partial<RouteDTO>)?.instanceType;

      if (link.url) {
        this.filteredNavLinks.push(link);
      } else if (module === 'non_module') {
        this.filteredNavLinks.push(link);
      } else if (module) {
        this.pushToFilteredNavLinksArrayBasedOnPermissions(
          module,
          instanceType,
          link
        );
      } else {
        this.filteredNavLinks.push(link);
      }
    }

    return this.filteredNavLinks;
  }

  private getNavigationLinksBasedOnContext() {
    switch (this.applicationContext) {
      case ApplicationContext.JTMS:
        return jtmsNavigationLinks;
      case ApplicationContext.JAAS:
        return myJBINavbarLinks;
      default:
        return myJBINavbarLinks;
    }
  }

  private pushToFilteredNavLinksArrayBasedOnPermissions(
    module: string | undefined,
    instanceType: EntityTypes | undefined,
    link: CustomNavLink
  ) {
    if (!module) {
      return;
    }

    if (this.checkForImplicitAccess(module)) {
      this.filteredNavLinks.push(link);
      return;
    }

    if (instanceType) {
      const isJbicModule = module.startsWith('jbic_management');
      const subdivisionPattern = isJbicModule
        ? `subdivision_\\w+_${instanceType}s-(images|events)-(read|view)`
        : `subdivision_\\w+_${instanceType}s(?:-(images|events))?-(read|view)`;

      const pattern = isJbicModule
        ? `${instanceType}_\\d*-(images|events)-(read|view).*`
        : `${instanceType}_\\d*(?:-(images|events))?-(read|view).*`;

      const regexExpression = new RegExp(pattern, 'i');

      const regexExpressionForSubdivision = new RegExp(subdivisionPattern, 'i');

      for (const readModule of this.readModules) {
        if (
          regexExpression.test(readModule) ||
          regexExpressionForSubdivision.test(readModule)
        ) {
          this.filteredNavLinks.push(link);
          break;
        }
      }
    }

    if (this.rootModules.length === 0) {
      return;
    }

    if (this.rootModules.includes(module)) {
      this.filteredNavLinks.push(link);
      return;
    }
  }

  private checkForImplicitAccess(targetModule: string): boolean {
    for (const module of this.readModules) {
      if (this.hasImplicitMatch(module, targetModule)) {
        return true;
      }
    }

    return false;
  }

  private hasImplicitMatch(module: string, targetModule: string): boolean {
    return module.startsWith(targetModule);
  }
}
